/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';

import {
  useStateField,
  useStateCode,
  useStateName,
} from '~state/getStateField';
import registerFields from '~state/registerFields';
import { FuturevoterEligibleStatement } from '~components/Futurevoter/FuturevoterEligibleContent';
import {
  ineligibleStates,
  registrationNotRequired,
  registrationPrintMailDisabled,
  registrationLeoReferralIfWillNotBe18,
  registrationNoStateFormAvailable,
} from './specialCases';
import { CustomizableButton, ButtonGroup } from '../common/Button';
import Markdown from '../common/Markdown';
import ContentDiv from '../common/ContentDiv';
import useRegisterRedirect from './useRegisterRedirect';

function IneligibleState({ tool, successPage }) {
  const registrationDirections = useStateField('registration_directions_mail');
  const stateCode = useStateCode();
  const stateName = useStateName();

  useRegisterRedirect('ineligible-state', tool);
  if (
    !stateCode ||
    (!ineligibleStates.includes(stateCode) &&
      !registrationLeoReferralIfWillNotBe18.includes(stateCode) &&
      !registrationNoStateFormAvailable.includes(stateCode))
  ) {
    return null;
  }

  const successUrl =
    successPage === 'donate'
      ? `/tools/${tool}/donate/`
      : `/tools/${tool}/share/`;

  const leoUrl = registerFields.region
    ? `/local-election-offices/${stateCode}/${registerFields.region}/`
    : `/local-election-offices/${stateCode}/`;

  const noOnlineRegistrationHeader =
    tool === 'futurevoter'
      ? 'Good news and bad news'
      : `${stateName} does not have online voter registration`;

  const header = registrationNotRequired.includes(stateCode)
    ? "You don't need to register to vote"
    : noOnlineRegistrationHeader;

  const includeLeoReferral =
    registrationPrintMailDisabled.includes(stateCode) ||
    registrationLeoReferralIfWillNotBe18.includes(stateCode) ||
    registrationNoStateFormAvailable.includes(stateCode);

  return (
    <ContentDiv>
      <h1>{header}</h1>
      {includeLeoReferral && tool === 'futurevoter' && (
        <>
          <FuturevoterEligibleStatement ineligibleState />

          {registrationPrintMailDisabled.includes(stateCode) && (
            <p>
              The bad news is that {stateName} does not have online voter
              registration.
            </p>
          )}

          {registrationLeoReferralIfWillNotBe18.includes(stateCode) && (
            <p>
              The bad news is that {stateName}&apos;s online tool and paper
              registration forms are not set up for registrants who won&apos;t
              be 18 by Election Day. If you want to go ahead and register now,
              you should contact your local election office.
            </p>
          )}

          {registrationNoStateFormAvailable.includes(stateCode) && (
            <p>
              The bad news is that you can&apos;t do this online or through the
              mail right now. If you want to go ahead and register now, you
              should contact your local election office and ask about in-person
              registration.
            </p>
          )}
        </>
      )}
      {registrationDirections &&
        registrationDirections.toUpperCase() !== 'N/A' && (
          <Markdown content={registrationDirections} />
        )}
      <p className='font-bold'>We have emailed detailed instructions to you.</p>
      <ButtonGroup>
        {includeLeoReferral && (
          <CustomizableButton
            href={leoUrl}
            target='_blank'
            rel='noopener'
            data-element-id='btn_register_ineligible-state_lookup'
            elementType='a'
          >
            Contact your local election office
          </CustomizableButton>
        )}
        <CustomizableButton
          to={successUrl}
          state={{ variant: 'info' }}
          elementType='Link'
          isSecondary={includeLeoReferral}
        >
          Continue
        </CustomizableButton>
      </ButtonGroup>
    </ContentDiv>
  );
}

IneligibleState.propTypes = {
  tool: PropTypes.oneOf(['register', 'futurevoter']),
  successPage: PropTypes.oneOf(['donate', 'share']),
};

IneligibleState.defaultProps = {
  tool: 'register',
};

export default IneligibleState;
